// ============================================================================
// src/lib/globalNav.js — declarative global navigation config (CC#10 lane)
// ----------------------------------------------------------------------------
// ONE source of truth for the persistent global header navigation:
//
//   • GLOBAL_DESTINATIONS  — the nine bar destinations, in bar order.
//   • SECTIONS_BY_PROGRAM  — the white row-2 section tabs per program.
//   • deriveNavSelection() — pure route/view -> {navKey, program, sections}.
//   • filterGlobalDestinations() — pure permission filter.
//
// Why this module exists: before the global-nav lane, Header.jsx carried six
// ad-hoc `inX` booleans plus four inline section arrays, and the program
// destinations lived a second time in HomeDashboard's tile wall. Selection was
// re-derived per surface. Everything now derives from the config below.
//
// SELECTION IS DERIVED, NEVER STORED. There is no `currentProgram` state: the
// caller passes the live `view` (owned by App's compatibility URL adapter)
// plus the canonical view->program map, and gets the selection back. Adding a second
// source of truth for "which program am I in" is exactly what this replaces.
//
// PERMISSIONS ARE DELEGATED, NEVER RESTATED. This module holds no role literal
// beyond PROCESSING_ROLES (which App imports, so that list exists once).
// filterGlobalDestinations() calls predicates supplied by App — canAccessProgram
// (profiles.program_access), canLightAccessView (the Light containment
// allowlist), isAdmin — so the bar can never drift from, or widen, the guards
// that App and the server already enforce. A destination is shown only if
// its own gate says yes; nothing here grants access.
// ============================================================================

// ---------------------------------------------------------------------------
// VIEW -> PROGRAM. The authoritative classification of every program-scoped
// view, moved here from App so ONE view has ONE program classification.
// App imports this for its program_access redirect guard; the header
// imports it to derive the selected chip. There is no second map: the bar's
// selection and App's access bounce read the same object.
//
// Views absent from this map (home, tasks, activity, processing, pastureMap,
// webforms, the public form hubs, ...) are not program-scoped and are never
// subject to the program_access bounce.
// ---------------------------------------------------------------------------
export const VIEW_TO_PROGRAM = Object.freeze({
  broilerHome: 'broiler',
  timeline: 'broiler',
  list: 'broiler',
  feed: 'broiler',
  broilerdailys: 'broiler',
  broilerweighins: 'broiler',
  layersHome: 'layer',
  layerbatches: 'layer',
  layerdailys: 'layer',
  eggdailys: 'layer',
  layers: 'layer',
  pigsHome: 'pig',
  breeding: 'pig',
  farrowing: 'pig',
  sows: 'pig',
  pigbatches: 'pig',
  pigs: 'pig',
  pigfeed: 'pig',
  pigdailys: 'pig',
  pigweighins: 'pig',
  cattleHome: 'cattle',
  cattleherds: 'cattle',
  cattledailys: 'cattle',
  cattleweighins: 'cattle',
  cattlebreeding: 'cattle',
  cattleforecast: 'cattle',
  cattlebatches: 'cattle',
  cattlelog: 'cattle',
  sheepHome: 'sheep',
  sheepflocks: 'sheep',
  sheepbatches: 'sheep',
  sheepdailys: 'sheep',
  sheepweighins: 'sheep',
  equipmentHome: 'equipment',
});

// Roles allowed on the cross-program Processing Calendar. Role-gated, NOT
// program_access-gated: a restricted program list must not deny Processing.
// App imports this so the list is defined exactly once.
export const PROCESSING_ROLES = Object.freeze(['farm_team', 'management', 'admin']);

export function isProcessingRole(role: string | null | undefined): boolean {
  return !!role && PROCESSING_ROLES.includes(role);
}

// Gate kinds. Each maps to a predicate supplied by App in
// filterGlobalDestinations(); the strings are just the wiring.
export const NAV_GATE = Object.freeze({
  PROGRAM: 'program', // profiles.program_access via canAccessProgram()
  OPERATIONAL: 'operational', // PROCESSING_ROLES
  ADMIN: 'admin', // role === 'admin'
  ANY: 'any', // every authenticated role; server RPC/RLS owns the real gate
});

// The ten global destinations, in bar order (README "Anatomy — desktop").
//
//   key         stable id, also the DOM hook (data-global-nav-chip)
//   label       full name (menu / aria)
//   shortLabel  what the bar renders — "Pasture Map" is shortened to "Pasture"
//               to buy horizontal room; the page keeps its full title
//   view        the canonical view string handed to setView()
//   program     VIEW_TO_PROGRAM key, or null for non-program surfaces
//   speciesKey  programColors key for the 7px dot; null = utility chip, no dot
//   overflow    true = may collapse into the hamburger in the tight desktop
//               band (see index.html). Admin is the one lowest-priority
//               destination. Processing stays on the bar at every desktop
//               width: it is an operational destination, not a Webform
//               (Ronnie, 2026-10-20).
// The four gate strings, derived from NAV_GATE so a new gate cannot be added to
// the enum without the destination type accepting it (and vice versa).
export type NavGate = (typeof NAV_GATE)[keyof typeof NAV_GATE];

export type GlobalDestination = {
  key: string;
  label: string;
  shortLabel: string;
  view: string;
  program: string | null;
  speciesKey: string | null;
  gate: NavGate;
  overflow: boolean;
};

export type NavSection = {view: string; label: string};

export const GLOBAL_DESTINATIONS: readonly GlobalDestination[] = Object.freeze([
  {
    key: 'broiler',
    label: 'Broilers',
    shortLabel: 'Broilers',
    view: 'broilerHome',
    program: 'broiler',
    speciesKey: 'broiler',
    gate: NAV_GATE.PROGRAM,
    overflow: false,
  },
  {
    key: 'layer',
    label: 'Layers',
    shortLabel: 'Layers',
    view: 'layersHome',
    program: 'layer',
    speciesKey: 'layer',
    gate: NAV_GATE.PROGRAM,
    overflow: false,
  },
  {
    key: 'pig',
    label: 'Pigs',
    shortLabel: 'Pigs',
    view: 'pigsHome',
    program: 'pig',
    speciesKey: 'pig',
    gate: NAV_GATE.PROGRAM,
    overflow: false,
  },
  {
    key: 'cattle',
    label: 'Cattle',
    shortLabel: 'Cattle',
    view: 'cattleHome',
    program: 'cattle',
    speciesKey: 'cattle',
    gate: NAV_GATE.PROGRAM,
    overflow: false,
  },
  {
    key: 'sheep',
    label: 'Sheep',
    shortLabel: 'Sheep',
    view: 'sheepHome',
    program: 'sheep',
    speciesKey: 'sheep',
    gate: NAV_GATE.PROGRAM,
    overflow: false,
  },
  {
    key: 'equipment',
    label: 'Equipment',
    shortLabel: 'Equipment',
    view: 'equipmentHome',
    program: 'equipment',
    // Utility chip: no species dot (A12 — species read as dot + label; the
    // equipment slate gray is not a species).
    speciesKey: null,
    gate: NAV_GATE.PROGRAM,
    overflow: false,
  },
  {
    key: 'pasture',
    label: 'Pasture Map',
    shortLabel: 'Pasture',
    view: 'pastureMap',
    program: null,
    speciesKey: null,
    // Cross-species field tool. Every authenticated role including Light
    // (migration 139 gives Light pasture farm-team-level access); the pasture
    // SECDEF RPCs own the real boundary.
    gate: NAV_GATE.ANY,
    overflow: false,
  },
  {
    key: 'projects',
    label: 'Projects',
    shortLabel: 'Projects',
    view: 'projects',
    program: null,
    speciesKey: null,
    // Farm jobs on a calendar plus undated ideas (owner decision D9): every
    // active role, Light and Equipment Tech included, next to Pasture. The
    // Projects RPCs own each write; nothing here restates a permission.
    gate: NAV_GATE.ANY,
    overflow: false,
  },
  {
    key: 'processing',
    label: 'Processing',
    shortLabel: 'Processing',
    view: 'processing',
    program: null,
    speciesKey: null,
    gate: NAV_GATE.OPERATIONAL,
    overflow: false,
  },
  {
    key: 'admin',
    label: 'Admin',
    shortLabel: 'Admin',
    view: 'webforms',
    program: null,
    speciesKey: null,
    gate: NAV_GATE.ADMIN,
    overflow: true,
  },
]);

// Row-2 section tabs per program. Lifted verbatim from the pre-lane inline
// arrays in Header.jsx — order, view strings, and labels are unchanged.
//
// Equipment is deliberately absent: /fleet renders its own Fleet/Fuel Log tab
// strip inside the page, so a header section row there would be a redundant
// second strip back-to-back with it. Pasture / Processing / Admin own their own
// in-page tabs the same way.
export const SECTIONS_BY_PROGRAM: Readonly<Record<string, readonly NavSection[]>> = Object.freeze({
  broiler: Object.freeze([
    {view: 'broilerHome', label: 'Dashboard'},
    {view: 'timeline', label: 'Timeline'},
    {view: 'list', label: 'Batches'},
    {view: 'broilerdailys', label: 'Dailys'},
    {view: 'broilerweighins', label: 'Weigh-Ins'},
    {view: 'feed', label: 'Poultry Feed'},
  ]),
  layer: Object.freeze([
    {view: 'layersHome', label: 'Dashboard'},
    {view: 'layerbatches', label: 'Layer Batches'},
    {view: 'layerdailys', label: 'Layer Dailys'},
    {view: 'eggdailys', label: 'Egg Dailys'},
  ]),
  pig: Object.freeze([
    {view: 'pigsHome', label: 'Dashboard'},
    {view: 'breeding', label: 'Timeline'},
    {view: 'farrowing', label: 'Farrowing'},
    {view: 'sows', label: 'Breeding Pigs'},
    {view: 'pigbatches', label: 'Batches'},
    {view: 'pigdailys', label: 'Dailys'},
    {view: 'pigweighins', label: 'Weigh-Ins'},
    {view: 'pigs', label: 'Feed'},
  ]),
  cattle: Object.freeze([
    {view: 'cattleHome', label: 'Dashboard'},
    {view: 'cattleherds', label: 'Herds'},
    {view: 'cattlebreeding', label: 'Breeding'},
    {view: 'cattleweighins', label: 'Weigh-Ins'},
    {view: 'cattleforecast', label: 'Forecast'},
    {view: 'cattlebatches', label: 'Batches'},
    {view: 'cattledailys', label: 'Dailys'},
    {view: 'cattlelog', label: 'Log'},
  ]),
  sheep: Object.freeze([
    {view: 'sheepHome', label: 'Dashboard'},
    {view: 'sheepflocks', label: 'Flocks'},
    {view: 'sheepweighins', label: 'Weigh-Ins'},
    {view: 'sheepdailys', label: 'Dailys'},
    {view: 'sheepbatches', label: 'Batches'},
  ]),
});

// program key -> destination key. Every program destination is its program's
// bar chip, so a program view lights the right chip without a second map.
const PROGRAM_TO_NAV_KEY: Readonly<Record<string, string>> = Object.freeze(
  GLOBAL_DESTINATIONS.reduce<Record<string, string>>((acc, d) => {
    if (d.program) acc[d.program] = d.key;
    return acc;
  }, {}),
);

// The three non-program chips (Pasture, Processing, Admin). Derived from
// GLOBAL_DESTINATIONS above — NOT a second hand-maintained classification.
// These views are deliberately absent from VIEW_TO_PROGRAM because they carry
// no program_access dimension, so they need this lookup to light their chip.
// Record pages that belong to a utility destination light that destination
// (A40: an Admin equipment record lives under Admin).
const UTILITY_RECORD_VIEW_ALIASES: Readonly<Record<string, string>> = Object.freeze({equipmentAdminRecord: 'admin'});
const UTILITY_VIEW_TO_NAV_KEY: Readonly<Record<string, string>> = Object.freeze({
  ...GLOBAL_DESTINATIONS.reduce<Record<string, string>>((acc, d) => {
    if (!d.program) acc[d.view] = d.key;
    return acc;
  }, {}),
  ...UTILITY_RECORD_VIEW_ALIASES,
});

/**
 * Derive the current global selection from the live view. Pure.
 *
 * @param {object}   opts
 * @param {string}   opts.view            current view string (URL-derived)
 * @param {object}  [opts.viewToProgram]  defaults to the canonical map above
 * @param {boolean} [opts.showForm]       broiler batch form drawer open
 * @param {boolean} [opts.showBreedForm]  pig breeding form drawer open
 * @param {boolean} [opts.showFarrowForm] pig farrowing form drawer open
 * @param {boolean} [opts.isLight]        Light role (no section row at all)
 * @param {function}[opts.canAccessProgram] program_access predicate. REQUIRED to
 *   expose a program section row — omitted/null/non-function fails closed.
 * @param {string[]} [opts.dailyReportSectionViews] views reachable without the
 *   program itself; injected, and null/empty keeps the fail-closed behaviour.
 * @returns {{navKey: string|null, program: string|null,
 *            sections: Array, activeSectionView: string|null}}
 */
export type NavSelectionOptions = {
  view?: string | null;
  viewToProgram?: Readonly<Record<string, string>> | null;
  showForm?: boolean;
  showBreedForm?: boolean;
  showFarrowForm?: boolean;
  isLight?: boolean;
  canAccessProgram?: ((program: string | null) => boolean) | null;
  /**
   * Views this caller may reach even when canAccessProgram is false for the
   * program. Injected by the caller from the access module; null/empty means no
   * carve-out, which is the pre-existing fail-closed behaviour.
   */
  dailyReportSectionViews?: readonly string[] | null;
};

export type NavSelection = {
  navKey: string | null;
  program: string | null;
  sections: readonly NavSection[];
  activeSectionView: string | null;
};

export function deriveNavSelection({
  view,
  viewToProgram = VIEW_TO_PROGRAM,
  showForm = false,
  showBreedForm = false,
  showFarrowForm = false,
  isLight = false,
  canAccessProgram = null,
  dailyReportSectionViews = null,
}: NavSelectionOptions = {}): NavSelection {
  // Annotated on the local, never on VIEW_TO_PROGRAM itself: a static guard
  // pins `export const VIEW_TO_PROGRAM = Object.freeze({`, and widening that
  // declaration to satisfy one dynamic lookup would edit the pinned line.
  const map: Readonly<Record<string, string>> = viewToProgram || VIEW_TO_PROGRAM;
  // Open form drawers are program context even though `view` may still name
  // the list behind them — matches the pre-lane inPoultry/inPigs behavior.
  let program: string | null = null;
  if (showForm) program = 'broiler';
  else if (showBreedForm || showFarrowForm) program = 'pig';
  else if (view && map[view]) program = map[view];

  const navKey = program ? PROGRAM_TO_NAV_KEY[program] || null : (view && UTILITY_VIEW_TO_NAV_KEY[view]) || null;

  // Section row. Light gets NO section row for ANY program: its allowlisted
  // views (broilerdailys, cattlelog, ...) map to a program, but every other tab
  // in that program's strip is a surface Light is bounced out of. Pre-lane this
  // carve-out existed for cattle only, so a Light user on /broiler/dailys was
  // shown five tabs that all bounced home. Generalizing the carve-out is a
  // tightening, never a widening.
  // Program access FAILS CLOSED: a missing, null, or non-function predicate is
  // not permission, it is the absence of an answer, so no program section row is
  // exposed. Anything else would let a wiring mistake (a forgotten prop, a
  // renamed field) silently publish a program's whole tab strip.
  let sections: readonly NavSection[] = [];
  const programAccessible = typeof canAccessProgram === 'function' && !!canAccessProgram(program);
  if (program && !isLight && programAccessible) {
    sections = SECTIONS_BY_PROGRAM[program] || [];
  } else if (
    program &&
    !isLight &&
    // The predicate must have ANSWERED false, not be missing. Without this a
    // wiring slip (forgotten prop, renamed field) would publish daily tabs
    // instead of failing closed, which is the opposite of what the
    // programAccessible fail-closed rule above exists to do.
    typeof canAccessProgram === 'function' &&
    dailyReportSectionViews &&
    dailyReportSectionViews.length > 0
  ) {
    // Daily-report carve-out (product decision 2026-09-08). A caller who reaches
    // this program's DAILY surfaces without holding the program itself still
    // needs a way to move between them — otherwise the page opens and the tab
    // strip is empty, which is a page you can land on but cannot navigate.
    //
    // Only the daily tabs are exposed. Showing the whole strip would render tabs
    // that bounce, which is the exact bug the Light carve-out was added to fix.
    //
    // The eligible view list is INJECTED, like viewToProgram and
    // canAccessProgram: this module states no role rule of its own beyond
    // PROCESSING_ROLES, so a null or empty list simply means no carve-out and the
    // pre-existing fail-closed behaviour stands.
    sections = (SECTIONS_BY_PROGRAM[program] || []).filter((s) => dailyReportSectionViews.includes(s.view));
  }

  // A drawer is open => no section tab is active (pre-lane: `active = view === v
  // && !showForm`). Otherwise the active tab is the one matching `view`.
  const drawerOpen = showForm || showBreedForm || showFarrowForm;
  // `view != null` is new text, not new behaviour: every section's `view` is a
  // non-empty string, so some() was already false whenever `view` was absent
  // and this branch already produced null.
  const activeSectionView = !drawerOpen && view != null && sections.some((s) => s.view === view) ? view : null;

  return {navKey, program, sections, activeSectionView};
}

/**
 * Coerce whatever a caller passed into a safe destination array. The permission
 * filter can legitimately produce an empty list (a role with no reachable
 * destination), and a wiring slip can produce undefined; global chrome that
 * every authenticated route mounts must not throw on either. Pure, and exported
 * so the render path's normalization is directly testable in a node
 * environment (this repo runs vitest without a DOM).
 *
 * @param {*} value
 * @returns {Array}
 */
export function normalizeDestinations<T = GlobalDestination>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

/**
 * Filter the nine destinations to the ones this user may actually reach. Pure;
 * every decision delegates to a predicate the caller supplies.
 *
 * @param {object}    ctx
 * @param {boolean}  [ctx.isLight]             Light role
 * @param {boolean}  [ctx.isAdmin]             admin role
 * @param {string}   [ctx.role]                raw role string (Processing gate)
 * @param {function} [ctx.canAccessProgram]    App's program_access predicate
 * @param {function} [ctx.canLightAccessView]  App's Light containment allowlist
 * @returns {Array} the visible subset, in bar order
 */
export type GlobalDestinationFilterContext = {
  isLight?: boolean;
  isAdmin?: boolean;
  role?: string | null;
  canAccessProgram?: ((program: string | null) => boolean) | null;
  canLightAccessView?: ((view: string) => boolean) | null;
};

export function filterGlobalDestinations({
  isLight = false,
  isAdmin = false,
  role = null,
  canAccessProgram = null,
  canLightAccessView = null,
}: GlobalDestinationFilterContext = {}): GlobalDestination[] {
  return GLOBAL_DESTINATIONS.filter((d) => {
    // Light containment runs FIRST and fails closed: a Light user sees a chip
    // only if that exact view is on App's Light allowlist. With no allowlist
    // supplied there is nothing to prove the view is permitted, so nothing
    // shows. This is what keeps the bar from becoming a bypass surface on
    // LightHomePortal, which renders the same Header.
    if (isLight) {
      if (typeof canLightAccessView !== 'function') return false;
      return canLightAccessView(d.view);
    }
    switch (d.gate) {
      case NAV_GATE.PROGRAM:
        // Fails closed, exactly like the section row above: only a supplied
        // predicate that positively grants access publishes a program chip.
        return typeof canAccessProgram === 'function' && !!canAccessProgram(d.program);
      case NAV_GATE.OPERATIONAL:
        return isProcessingRole(role);
      case NAV_GATE.ADMIN:
        return !!isAdmin;
      case NAV_GATE.ANY:
        return true;
      default:
        // Unknown gate fails closed rather than defaulting to visible.
        return false;
    }
  });
}
